// Cause & Effect — the site-level rule list and interlock matrix.
//
// NOT YET ROUTED. This page is deliberately unmounted: adding it needs two additive lines in
// App.js (import + <Route path="/automation" element={<AutomationRules />} />) and one entry in
// Sidebar.jsx's Monitoring group. Neither file is touched by this change so nothing in the live
// build moves until someone chooses to wire it up.
//
// Backend: SPEC_AUTOMATION_CAUSE_EFFECT.md §9. Until those endpoints exist the page loads real
// devices and says plainly that the rules API is missing — it never invents rules to fill space.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Zap, Plus, ServerCrash, Loader2, RefreshCw, Grid3x3, List, FlaskConical,
  PlayCircle, PauseCircle, ShieldAlert, History, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import { useSiteContext } from "../components/layout/Layout";
import { useAuth } from "../context/AuthContext";
import { automationApi } from "../lib/automationApi";
import { describeSource, sourceKey } from "../lib/automationModel";
import { useAutomationCatalogue } from "../components/automation/useAutomationCatalogue";
import { SafetyClassBadge, ModeBadge, BreakerBadge, Chip } from "../components/automation/badges";
import RuleSentence from "../components/automation/RuleSentence";
import RuleBuilder from "../components/automation/RuleBuilder";

// Spec §9 — arming a control rule is a higher bar than creating one.
const CREATE_ROLES = ["super_admin", "admin"];
const ARM_ROLES = ["super_admin", "admin", "customer_admin"];
const SIMULATE_ROLES = ["super_admin", "admin", "customer_admin", "site_manager", "building_manager"];

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "medium" });
  } catch {
    return iso;
  }
}

// ── Run log ───────────────────────────────────────────────────────────────────

const OUTCOME_TONE = {
  fired: "red",
  would_have_fired: "sky",
  feedback_ok: "emerald",
  reset: "gray",
  feedback_timeout: "red",
  breaker_tripped: "red",
  blocked_by_permissive: "amber",
  blocked_by_cooldown: "amber",
  blocked_by_override: "amber",
  blocked_by_priority: "amber",
};

function RunLog({ ruleId }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await automationApi.listRuns(ruleId, { limit: 20 });
      if (!alive) return;
      setRuns(r.ok ? r.data || [] : []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [ruleId]);

  if (loading) {
    return <div className="flex items-center gap-2 text-xs text-gray-500 py-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading history…</div>;
  }
  if (!runs.length) {
    return <div className="text-xs text-gray-500 py-3">No evaluations recorded yet.</div>;
  }
  return (
    <div className="overflow-x-auto mt-2">
      <table className="w-full text-xs min-w-[520px]">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
            <th className="text-left py-1.5 pr-3 font-bold">Time</th>
            <th className="text-left py-1.5 pr-3 font-bold">Outcome</th>
            <th className="text-left py-1.5 font-bold">Detail</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.run_id} className="border-b border-gray-100 last:border-0">
              <td className="py-1.5 pr-3 font-mono text-gray-500 whitespace-nowrap">{fmtTime(run.at)}</td>
              <td className="py-1.5 pr-3">
                <Chip tone={OUTCOME_TONE[run.outcome] || "gray"}>{(run.outcome || "").replace(/_/g, " ")}</Chip>
              </td>
              <td className="py-1.5 text-gray-600">{run.note || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Rule card ─────────────────────────────────────────────────────────────────

function RuleCard({ rule, ctx, onEdit, onSetMode, onReset, onTest, busy, canArm, canSimulate }) {
  const [showLog, setShowLog] = useState(false);
  const armed = rule.mode === "armed";

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-extrabold text-base truncate">{rule.name}</h3>
            <SafetyClassBadge value={rule.safety_class} />
            <ModeBadge value={rule.mode} />
            {rule.breaker_tripped && <BreakerBadge reason={rule.breaker_tripped_reason} />}
            {rule.latched && <Chip tone="amber">Latched</Chip>}
          </div>
          {rule.description && <p className="text-xs text-gray-500 mt-1">{rule.description}</p>}
        </div>
        <button
          type="button"
          onClick={onEdit}
          className="text-xs text-orange-600 hover:text-orange-700 font-semibold shrink-0"
        >
          Edit
        </button>
      </div>

      <div className="mt-3 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
        <RuleSentence rule={rule} ctx={ctx} />
      </div>

      <div className="flex items-center gap-2 flex-wrap mt-3">
        {canSimulate && rule.mode !== "simulate" && (
          <Button size="sm" variant="outline" className="text-xs" disabled={busy} onClick={() => onSetMode("simulate")}>
            <FlaskConical className="w-3.5 h-3.5 mr-1" /> Simulate
          </Button>
        )}
        {canArm && !armed && (
          <Button size="sm" className="text-xs bg-red-600 hover:bg-red-700 text-white" disabled={busy} onClick={() => onSetMode("armed")}>
            <PlayCircle className="w-3.5 h-3.5 mr-1" /> Arm
          </Button>
        )}
        {canSimulate && rule.mode !== "disabled" && (
          <Button size="sm" variant="outline" className="text-xs" disabled={busy} onClick={() => onSetMode("disabled")}>
            <PauseCircle className="w-3.5 h-3.5 mr-1" /> Disable
          </Button>
        )}
        <Button size="sm" variant="ghost" className="text-xs" disabled={busy} onClick={onTest}>
          Dry run
        </Button>
        {(rule.breaker_tripped || rule.latched) && canSimulate && (
          <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200" disabled={busy} onClick={onReset}>
            <ShieldAlert className="w-3.5 h-3.5 mr-1" /> Clear {rule.breaker_tripped ? "breaker" : "latch"}
          </Button>
        )}
        <button
          type="button"
          onClick={() => setShowLog((v) => !v)}
          className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1 ml-auto"
        >
          <History className="w-3.5 h-3.5" /> {showLog ? "Hide" : "History"}
        </button>
      </div>

      {rule.armed_by && (
        <div className="text-[11px] text-gray-400 mt-2">
          Armed by {rule.armed_by} · {fmtTime(rule.armed_at)}
        </div>
      )}

      {showLog && <RunLog ruleId={rule.rule_id} />}
    </div>
  );
}

// ── Matrix ────────────────────────────────────────────────────────────────────
//
// Inputs down the left, outputs across the top — the printable artifact a fire consultant asks
// for at handover. Built from the rules themselves, so it can never drift from what the engine
// will actually do.

function Matrix({ rules, ctx }) {
  const { inputs, outputs, cells } = useMemo(() => {
    const inputMap = new Map();
    const outputMap = new Map();
    const cellMap = new Map();

    for (const rule of rules) {
      const ins = (rule.trigger?.all || []).map((c) => c.source).filter(Boolean);
      const outs = (rule.actions || []).filter((a) => a.type === "write").map((a) => a.target).filter(Boolean);
      for (const i of ins) inputMap.set(sourceKey(i), i);
      for (const o of outs) outputMap.set(sourceKey(o), o);
      for (const i of ins) {
        for (const o of outs) {
          const key = `${sourceKey(i)}→${sourceKey(o)}`;
          if (!cellMap.has(key)) cellMap.set(key, []);
          cellMap.get(key).push(rule);
        }
      }
    }
    return {
      inputs: Array.from(inputMap.entries()),
      outputs: Array.from(outputMap.entries()),
      cells: cellMap,
    };
  }, [rules]);

  if (!outputs.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center text-sm text-gray-500">
        No rule drives an output yet, so there is nothing to plot.
        <div className="text-xs text-gray-400 mt-1">Supervisory rules raise alerts and do not appear on the matrix.</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 overflow-x-auto">
      <table className="text-xs border-collapse min-w-[560px]">
        <thead>
          <tr>
            <th className="text-left p-2 text-[10px] uppercase tracking-wider text-gray-500 font-bold border-b border-gray-200">
              Input ↓ / Output →
            </th>
            {outputs.map(([key, src]) => (
              <th key={key} className="p-2 text-left text-[11px] font-bold text-gray-700 border-b border-l border-gray-200 align-bottom min-w-[120px]">
                {describeSource(src, ctx)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {inputs.map(([ikey, isrc]) => (
            <tr key={ikey}>
              <td className="p-2 font-medium text-gray-700 border-b border-gray-100 whitespace-nowrap">
                {describeSource(isrc, ctx)}
              </td>
              {outputs.map(([okey]) => {
                const hits = cells.get(`${ikey}→${okey}`) || [];
                return (
                  <td key={okey} className="p-2 border-b border-l border-gray-100 align-top">
                    {hits.length === 0 ? (
                      <span className="text-gray-200">·</span>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {hits.map((r) => (
                          <div key={r.rule_id} className="flex items-center gap-1" title={r.name}>
                            <ArrowRight className="w-3 h-3 text-orange-500 shrink-0" strokeWidth={2.5} />
                            <ModeBadge value={r.mode} />
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AutomationRules() {
  const { selectedSite } = useSiteContext();
  const { user } = useAuth();
  const canCreate = CREATE_ROLES.includes(user?.role);
  const canArm = ARM_ROLES.includes(user?.role);
  const canSimulate = SIMULATE_ROLES.includes(user?.role);

  const { ctx, liveValues, loading: catalogueLoading } = useAutomationCatalogue(selectedSite);

  const [rules, setRules] = useState([]);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [busyRule, setBusyRule] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [armTarget, setArmTarget] = useState(null);

  const load = useCallback(async () => {
    if (!selectedSite) return;
    setLoading(true);
    const r = await automationApi.listRules({ site_id: selectedSite });
    setUnavailable(r.unavailable);
    setRules(r.ok ? r.data || [] : []);
    setLoading(false);
  }, [selectedSite]);

  useEffect(() => { load(); }, [load]);

  const save = async (rule) => {
    setSaving(true);
    setSaveError(null);
    const r = rule.rule_id
      ? await automationApi.updateRule(rule.rule_id, rule)
      : await automationApi.createRule(rule);
    setSaving(false);
    if (r.unavailable) {
      setSaveError("The automation API is not deployed on this server yet, so the rule was not saved. The logic you built is valid — see SPEC_AUTOMATION_BACKEND_CHANGES.md.");
      return;
    }
    if (!r.ok) { setSaveError(r.error); return; }
    toast.success(rule.rule_id ? "Rule saved" : "Rule created — it is disabled until you arm it");
    setBuilderOpen(false);
    setEditing(null);
    load();
  };

  const applyMode = async (rule, mode) => {
    setBusyRule(rule.rule_id);
    const r = await automationApi.setMode(rule.rule_id, mode);
    setBusyRule(null);
    if (r.unavailable) { toast.error("Automation API is not deployed yet"); return; }
    if (!r.ok) { toast.error(r.error || "Could not change mode"); return; }
    toast.success(`${rule.name} is now ${mode}`);
    load();
  };

  const setMode = (rule, mode) => {
    // Arming is the moment a rule gains the ability to move equipment. It gets a confirmation
    // naming what it will drive; nothing else does.
    if (mode === "armed") { setArmTarget(rule); return; }
    applyMode(rule, mode);
  };

  const resetRule = async (rule) => {
    setBusyRule(rule.rule_id);
    const r = await automationApi.resetRule(rule.rule_id);
    setBusyRule(null);
    if (!r.ok) { toast.error(r.unavailable ? "Automation API is not deployed yet" : r.error); return; }
    toast.success("Rule reset");
    load();
  };

  const testRule = async (rule) => {
    setBusyRule(rule.rule_id);
    const r = await automationApi.testRule(rule.rule_id);
    setBusyRule(null);
    if (!r.ok) { toast.error(r.unavailable ? "Automation API is not deployed yet" : r.error); return; }
    toast.success(r.data?.note || `Dry run: ${r.data?.outcome || "evaluated"}`);
  };

  if (!selectedSite) {
    return <div className="p-8 text-gray-500 text-center">Select a site to configure automation.</div>;
  }

  const armedCount = rules.filter((r) => r.mode === "armed").length;
  const simCount = rules.filter((r) => r.mode === "simulate").length;
  const trippedCount = rules.filter((r) => r.breaker_tripped).length;

  return (
    <div className="space-y-6 pb-10">

      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 p-6">
        <div className="max-w-7xl mx-auto flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs text-orange-600 uppercase tracking-wider font-bold flex items-center gap-1">
              <Zap className="w-3 h-3" strokeWidth={2.5} /> Interlocks
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold mt-1">Cause &amp; Effect</h1>
            <p className="text-sm text-gray-500 mt-1">
              Conditional logic linking input points to relay outputs — configured by hand, per device.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {!unavailable && (
              <div className="flex items-center gap-1.5 mr-1">
                {armedCount > 0 && <Chip tone="red">{armedCount} armed</Chip>}
                {simCount > 0 && <Chip tone="sky">{simCount} simulating</Chip>}
                {trippedCount > 0 && <Chip tone="red">{trippedCount} tripped</Chip>}
              </div>
            )}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setView("list")}
                className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1 ${view === "list" ? "bg-orange-50 text-orange-700" : "bg-white text-gray-500"}`}
              >
                <List className="w-3.5 h-3.5" /> Rules
              </button>
              <button
                type="button"
                onClick={() => setView("matrix")}
                className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1 border-l border-gray-200 ${view === "matrix" ? "bg-orange-50 text-orange-700" : "bg-white text-gray-500"}`}
              >
                <Grid3x3 className="w-3.5 h-3.5" /> Matrix
              </button>
            </div>
            <Button size="sm" variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
            {canCreate && !unavailable && (
              <Button
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={() => { setEditing(null); setSaveError(null); setBuilderOpen(true); }}
              >
                <Plus className="w-4 h-4 mr-1" /> New rule
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 space-y-4">

        {unavailable && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <ServerCrash className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" strokeWidth={2.5} />
            <div className="text-sm text-amber-900">
              <div className="font-bold">The automation API is not deployed on this server yet.</div>
              <p className="text-xs mt-1 text-amber-800">
                This page and the rule builder are complete and read your real devices, but the endpoints under
                <code className="font-mono mx-1">/api/automation</code> do not exist. Build them from
                <code className="font-mono mx-1">SPEC_AUTOMATION_BACKEND_CHANGES.md</code> and this page starts working
                with no frontend change.
              </p>
            </div>
          </div>
        )}

        {!unavailable && (loading || catalogueLoading) && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading rules…
          </div>
        )}

        {!unavailable && !loading && rules.length === 0 && (
          <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-10 text-center">
            <div className="text-sm text-gray-600 font-semibold">No rules configured at this site.</div>
            <p className="text-xs text-gray-500 mt-2 max-w-md mx-auto">
              Nothing here is created automatically. Every interlock is configured by hand against the specific
              devices at this site, because wiring, relay addressing and register naming differ everywhere.
            </p>
            {canCreate && (
              <Button
                className="mt-4 bg-orange-600 hover:bg-orange-700 text-white"
                onClick={() => { setEditing(null); setBuilderOpen(true); }}
              >
                <Plus className="w-4 h-4 mr-1" /> Create the first rule
              </Button>
            )}
          </div>
        )}

        {!unavailable && !loading && rules.length > 0 && (
          view === "list" ? (
            <div className="grid grid-cols-1 gap-3">
              {rules.map((rule) => (
                <RuleCard
                  key={rule.rule_id}
                  rule={rule}
                  ctx={ctx}
                  busy={busyRule === rule.rule_id}
                  canArm={canArm}
                  canSimulate={canSimulate}
                  onEdit={() => { setEditing(rule); setSaveError(null); setBuilderOpen(true); }}
                  onSetMode={(m) => setMode(rule, m)}
                  onReset={() => resetRule(rule)}
                  onTest={() => testRule(rule)}
                />
              ))}
            </div>
          ) : (
            <Matrix rules={rules} ctx={ctx} />
          )
        )}
      </div>

      <RuleBuilder
        open={builderOpen}
        onOpenChange={(v) => { setBuilderOpen(v); if (!v) { setEditing(null); setSaveError(null); } }}
        siteId={selectedSite}
        initialRule={editing}
        ctx={ctx}
        liveValues={liveValues}
        onSave={save}
        saving={saving}
        saveError={saveError}
      />

      <AlertDialog open={Boolean(armTarget)} onOpenChange={(v) => !v && setArmTarget(null)}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Arm &ldquo;{armTarget?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-gray-600">
                <p>
                  {armTarget?.safety_class === "supervisory"
                    ? "This rule will start raising alerts against live telemetry. It never writes to a device."
                    : "This rule will begin actuating equipment automatically, without an operator pressing anything."}
                </p>
                {armTarget && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <RuleSentence rule={armTarget} ctx={ctx} />
                  </div>
                )}
                {armTarget?.safety_class !== "supervisory" && (
                  <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                    Life-safety interlocks must remain hardwired at the panel. Arming this does not replace them.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => { const t = armTarget; setArmTarget(null); applyMode(t, "armed"); }}
            >
              Arm rule
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
